"use client";

import React, { useCallback, useEffect, useRef } from "react";
import { ReactSVG } from "react-svg";

interface LegacySvgHeatmapProps {
  mapUrl: string;
  /** Resource id -> fill colour. Resources not listed keep the map's own look. */
  colors: Record<string, string>;
  onHover: (resourceId: string | null) => void;
  /** Resources can be clicked (the caller listens), so they show a hand. */
  isPickable?: boolean;
}

const PAINTABLE = "path, rect, circle, ellipse, polygon";

/**
 * Legacy floor plans are hand-made SVGs where every resource is a
 * `<g id="reso_…">` under `g#resources`. Colouring the shapes inside those
 * groups is all a heatmap needs; the group boundaries double as hover
 * targets.
 */
export const LegacySvgHeatmap: React.FC<LegacySvgHeatmapProps> = ({
  mapUrl,
  colors,
  onHover,
  isPickable = false
}) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const colorsRef = useRef(colors);
  colorsRef.current = colors;
  const isPickableRef = useRef(isPickable);
  isPickableRef.current = isPickable;

  const paint = useCallback((svg: SVGSVGElement) => {
    const groups = svg.querySelectorAll<SVGGElement>(
      'g#resources g[id^="reso_"]'
    );
    for (const group of groups) {
      const color = colorsRef.current[group.id];
      if (!color) {
        continue;
      }
      for (const shape of group.querySelectorAll<SVGElement>(PAINTABLE)) {
        shape.style.fill = color;
        shape.style.fillOpacity = "1";
        shape.style.stroke = color;
      }
      group.style.cursor = isPickableRef.current ? "pointer" : "default";
    }
  }, []);

  const handleReady = useCallback(
    (err: Error | null, svg?: SVGSVGElement) => {
      if (err || !svg) {
        return;
      }
      svgRef.current = svg;
      svg
        .querySelectorAll<SVGGElement>('g#resources g[id^="reso_"]')
        .forEach((group) => {
          group.addEventListener("mouseenter", () => onHover(group.id));
          group.addEventListener("mouseleave", () => onHover(null));
        });
      paint(svg);
    },
    [onHover, paint]
  );

  useEffect(() => {
    if (svgRef.current) {
      paint(svgRef.current);
    }
  }, [paint, colors, isPickable]);

  return (
    <ReactSVG
      src={`${mapUrl}?t=svg`}
      afterInjection={handleReady}
      key={mapUrl}
    />
  );
};
